import { Box, Container, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { TOURNAMENT_DEFAULTS } from '../../../../../corelogic/domain/constants/tournament.constant';
import {
  CleanTournament,
  FrontendTournament,
  FrontendTournamentWithDetails,
  TournamentUpsert,
} from '../../../../../corelogic/domain/models/tournament.model';
import { extractSeasonFromTournamentName } from '../../../../../corelogic/domain/usecases/tournaments/extractSeasonFromTournamentName.usecase';
import {
  isFMMAPublicTournament,
  toFrontendTournament,
} from '../../../../../corelogic/domain/utils/tournament.utils';
import { useSeasonState } from '../../hooks/useSeasonState';
import { useTournamentState } from '../../hooks/useTournamentState';
import { useUserProfile } from '../../hooks/useUserProfile';
import { TournamentService } from '../../services/TournamentService';
import LoadingBar from '../Shared/LoadingBar';
import ReusableAlert from '../Shared/ReusableAlert/ReusableAlert';
import { SuccessModal } from '../Shared/SuccessModal/SuccessModal';
import CreateTournamentModal from './components/CreateTournamentModal';
import SearchTournamentModal from './components/SearchTournamentModal';
import TournamentDetails from './components/TournamentDetails';
import TournamentList from './components/TournamentList';

const styles = {
  container: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), url('/viewer_octagon.png')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    overflow: 'visible',
  },
  gridContainer: {
    height: 'calc(100vh - 80px)',
    alignItems: 'stretch',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: '10px',
    width: '100%',
    overflow: 'visible',
    paddingLeft: { xs: '10px', md: '10px' },
    paddingRight: { xs: '10px', md: '40px' },
    transition: 'height 0.3s, padding 0.3s',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingRight: { xs: '10px', md: '10px' },
    paddingLeft: { xs: '10px', md: '40px' },
    overflow: 'visible',
    transition: 'height 0.3s, padding 0.3s',
  },
};

const TournamentPage = () => {
  const { isLoading: isUserLoading, user } = useUserProfile();
  const { seasons, isLoading: isSeasonsLoading } = useSeasonState();
  const {
    allTournaments,
    userTournaments,
    userCreatedTournaments,
    tournamentDetails,
    isTournamentLoading,
    isUserTournamentLoading,
    isUserCreatedTournamentLoading,
    isTournamentDetailsLoading,
  } = useTournamentState();
  const currentUsername = user?.username;
  const [loading, setLoading] = useState(true);

  // Tournament List
  const [viewType, setViewType] = useState<'all' | 'active' | 'completed'>(
    'all',
  );
  const [tournamentType, setTournamentType] = useState<
    'all' | 'public' | 'private'
  >('all');

  // Tournament Creation and joining
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchModalTournaments, setSearchModalTournaments] = useState<
    FrontendTournament[]
  >([]);

  // Tournament Selection
  const [selectedTournamentId, setSelectedTournamentId] = useState<
    string | null
  >(null);
  const [selectedTournament, setSelectedTournament] =
    useState<FrontendTournament | null>(null);
  const [selectedTournamentDetails, setSelectedTournamentDetails] =
    useState<FrontendTournamentWithDetails | null>(null);

  // Success Modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Tournament List
  const [tournaments, setTournaments] = useState<FrontendTournament[]>([]);
  const [publicFMMATournamentIds, setPublicFMMATournamentIds] = useState<
    Set<string>
  >(new Set());

  // Reusable Alert
  const [snackbarText, setSnackbarText] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Auto-selection tracking
  const [tournamentToAutoSelect, setTournamentToAutoSelect] = useState<
    string | null
  >(null);

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  useEffect(() => {
    const tournamentsList: FrontendTournament[] = [];

    // Add user tournaments
    if (userTournaments) {
      const convertedUserTournaments = userTournaments.map(
        (tournament: CleanTournament) =>
          toFrontendTournament(tournament, seasons),
      );
      tournamentsList.push(...convertedUserTournaments);
    }

    // Add public FMMA tournaments from allTournaments that user is not already part of
    if (allTournaments) {
      const userTournamentIds = new Set(
        (userTournaments ?? []).map((t: CleanTournament) => t.id),
      );

      // Get public FMMA tournament IDs to filter them out later
      const publicFMMATournamentIds = new Set(
        allTournaments
          .filter(
            (tournament: CleanTournament) =>
              !tournament.private &&
              tournament.name.includes(
                TOURNAMENT_DEFAULTS.GLOBAL_TOURNAMENT_PREFIX,
              ),
          )
          .map((t: CleanTournament) => t.id),
      );

      const publicFMMATournaments = allTournaments
        .filter(
          (tournament: CleanTournament) =>
            !tournament.private &&
            tournament.name.includes(
              TOURNAMENT_DEFAULTS.GLOBAL_TOURNAMENT_PREFIX,
            ) &&
            !userTournamentIds.has(tournament.id),
        )
        .map((tournament: CleanTournament) =>
          toFrontendTournament(tournament, seasons),
        );

      tournamentsList.push(...publicFMMATournaments);

      // Store the public FMMA tournament IDs for filtering in search modal
      setPublicFMMATournamentIds(publicFMMATournamentIds);
    }

    setTournaments(tournamentsList);
  }, [allTournaments, userTournaments, userCreatedTournaments, seasons]);

  // Sort tournaments to show FMMA tournaments first
  const sortedTournaments = tournaments.sort((a, b) => {
    const aHasFMMA = a.name.includes(
      TOURNAMENT_DEFAULTS.GLOBAL_TOURNAMENT_PREFIX,
    );
    const bHasFMMA = b.name.includes(
      TOURNAMENT_DEFAULTS.GLOBAL_TOURNAMENT_PREFIX,
    );

    // If one has FMMA and the other doesn't, prioritize the one with FMMA
    if (aHasFMMA && !bHasFMMA) return -1;
    if (!aHasFMMA && bHasFMMA) return 1;

    // If both have FMMA or both don't have FMMA, maintain original order
    return 0;
  });

  // Handle data loading ux if loading
  useEffect(() => {
    if (
      !isUserLoading &&
      !isSeasonsLoading &&
      !isTournamentLoading &&
      !isUserTournamentLoading &&
      !isUserCreatedTournamentLoading
    ) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [
    isUserLoading,
    isSeasonsLoading,
    isTournamentLoading,
    isUserTournamentLoading,
    isUserCreatedTournamentLoading,
  ]);

  // Handle tournament filtering
  const filteredTournaments = sortedTournaments?.filter(
    (tournament: FrontendTournament) => {
      // Filter by active/all
      if (viewType === 'active') {
        // If no endDate is provided, consider it ongoing/active
        // If endDate exists, check if it's in the future
        if (tournament.endDate && tournament.endDate < new Date()) {
          return false;
        }
      }

      if (viewType === 'completed') {
        if (tournament.endDate && tournament.endDate > new Date()) {
          return false;
        }
      }

      // Filter by tournament type
      if (tournamentType === 'public' && tournament.private) {
        return false;
      }
      if (tournamentType === 'private' && !tournament.private) {
        return false;
      }

      return true;
    },
  );

  // Handle view type change
  const handleViewTypeChange = (
    newViewType: 'all' | 'active' | 'completed',
  ) => {
    setViewType(newViewType);
  };

  // Handle tournament type change
  const handleTournamentTypeChange = (
    newTournamentType: 'all' | 'public' | 'private',
  ) => {
    setTournamentType(newTournamentType);
  };

  // Handle tournament selection and fetch details
  const handleTournamentSelect = (tournament: FrontendTournament) => {
    if (selectedTournamentId === tournament.id) {
      return;
    }
    setSelectedTournamentId(tournament.id);
    setSelectedTournament(tournament);

    // check for global tournament parameters
    let tournamentYear = undefined;
    if (tournament.startDate) {
      const startDate = new Date(tournament.startDate);
      tournamentYear = startDate.getFullYear();
    }
    const isGlobal = isFMMAPublicTournament(
      tournament.name,
      tournament.creatorName,
      tournament.private,
    );
    if (!isGlobal) {
      TournamentService.retrieveTournamentDetails(
        tournament.id,
        tournament.seasonIds,
      );
    } else {
      const globalSeasonId = extractSeasonFromTournamentName(
        tournament.name,
        seasons,
      );
      TournamentService.retrieveTournamentDetails(
        tournament.id,
        tournament.seasonIds,
        isGlobal,
        tournamentYear,
        // if globalSeasonId is not null, use it, otherwise use the first seasonId
        globalSeasonId,
      );
    }
  };

  // dynamically update selected tournament details when tournament details are updated
  useEffect(() => {
    if (!selectedTournament) {
      console.warn('No tournament selected');
      return;
    }
    if (!tournamentDetails) {
      console.warn('No tournament details found');
      return;
    }
    const detailedTournament: FrontendTournamentWithDetails = {
      ...selectedTournament,
      participantDetails: tournamentDetails,
    };
    setSelectedTournamentDetails(detailedTournament);
  }, [selectedTournamentId, tournamentDetails, selectedTournament]);

  // Auto-select tournament when it appears in the tournaments array
  useEffect(() => {
    if (tournamentToAutoSelect && tournaments.length > 0) {
      const tournament = tournaments.find(
        (t) => t.id === tournamentToAutoSelect,
      );
      if (tournament) {
        console.log('Auto-selecting tournament:', tournament.name);
        handleTournamentSelect(tournament);
        setTournamentToAutoSelect(null); // Clear the auto-select flag
      }
    }
  }, [tournaments, tournamentToAutoSelect]);

  // Handle deselection of tournament
  const handleDeselectTournament = () => {
    setSelectedTournamentId(null);
    setSelectedTournamentDetails(null);
    setSelectedTournament(null);
  };

  const handleRefreshTournaments = () => {
    setSearchLoading(true);
    TournamentService.retrieveAllTournaments();
    setSearchLoading(false);
  };

  const handleOpenSearchModal = () => {
    if (!allTournaments) {
      console.warn('No tournaments found');
      return;
    }

    // Get a Set of user tournament IDs for fast lookup
    const userTournamentIds = new Set(
      (userTournaments ?? []).map((t: CleanTournament) => t.id),
    );

    // Filter allTournaments to exclude both user tournaments and public FMMA tournaments
    const tournamentsList = allTournaments
      .filter(
        (t: CleanTournament) =>
          !userTournamentIds.has(t.id) && !publicFMMATournamentIds.has(t.id),
      )
      .map((tournament: CleanTournament) =>
        toFrontendTournament(tournament, seasons),
      );
    setSearchModalTournaments(tournamentsList);
    setIsSearchModalOpen(true);
  };

  const handleLeaveTournamentResponse = (success: boolean) => {
    if (success) {
      setSuccessMessage('Tournament left successfully');
      setShowSuccessModal(true);
    } else {
      setSnackbarText('Failed to leave tournament');
      setOpenSnackbar(true);
    }
  };

  const handleUpsertTournamentResponse = (success: boolean) => {
    if (success) {
      setSuccessMessage('Tournament modified successfully');
      setShowSuccessModal(true);
    } else {
      setSnackbarText('Failed to modify tournament');
      setOpenSnackbar(true);
    }
  };

  // Add function to handle joining tournaments
  const handleJoinTournament = async (
    tournamentId: string,
    password?: string,
  ) => {
    console.log(
      `Joining tournament ${tournamentId}${password ? ' with password' : ''}`,
    );
    // find tournament in allTournaments
    const tournament = allTournaments?.find(
      (t: CleanTournament) => t.id === tournamentId,
    );
    if (!tournament) {
      setSnackbarText('Tournament not found');
      setOpenSnackbar(true);
      return;
    }
    if (!password && tournament.private) {
      setSnackbarText('Tournament is private - please provide a password');
      setOpenSnackbar(true);
      return;
    }
    if (tournament.private && password) {
      const passwordResult = await TournamentService.validateTournamentPassword(
        tournamentId,
        password,
      );
      if (!passwordResult) {
        setSnackbarText('Invalid password - please try again');
        setOpenSnackbar(true);
        return;
      }
    }
    const joinResult = await TournamentService.upsertUserTournament({
      tournamentId,
      joinDate: new Date(),
      totalPoints: 0,
    });
    if (joinResult) {
      setSuccessMessage('Tournament joined successfully');
      setShowSuccessModal(true);

      // Auto-select the tournament after successful join
      setTournamentToAutoSelect(tournamentId);
    } else {
      setSnackbarText('Failed to join tournament');
      setOpenSnackbar(true);
    }
  };

  // Tournament creation handler
  const handleCreateTournament = useCallback(
    async (tournamentData: TournamentUpsert) => {
      const result = await TournamentService.upsertTournament(tournamentData);
      if (result) {
        setSuccessMessage('Tournament created successfully');
        setShowSuccessModal(true);

        // Auto-select the created tournament using the returned tournament ID
        if (result && result.id) {
          const tournamentId = result.id;
          setTournamentToAutoSelect(tournamentId);
        }
      } else {
        setSnackbarText('Failed to create tournament');
        setOpenSnackbar(true);
      }
    },
    [tournaments, currentUsername, selectedTournamentId],
  );

  return (
    <Container maxWidth={false} disableGutters sx={styles.container}>
      {loading && <LoadingBar isLoading={true} />}

      <Grid container spacing={2} sx={styles.gridContainer}>
        {/* Left Side - Tournament List */}
        <Grid item xs={12} md={5.5} lg={5} sx={styles.leftColumn}>
          <TournamentList
            tournaments={filteredTournaments || []}
            onTournamentSelect={handleTournamentSelect}
            selectedTournamentId={selectedTournamentId}
            tournamentType={tournamentType}
            onTournamentTypeChange={handleTournamentTypeChange}
            viewType={viewType}
            onViewTypeChange={handleViewTypeChange}
            onCreateTournament={() => setIsCreateModalOpen(true)}
            onSearchTournament={handleOpenSearchModal}
          />
        </Grid>

        {/* Right Side - Tournament Details */}
        <Grid item xs={12} md={6.5} lg={7} sx={styles.rightColumn}>
          {isTournamentDetailsLoading && (
            <Box sx={{ position: 'relative', width: '100%', height: 0 }}>
              <LoadingBar isLoading={true} position="absolute" height={3} />
            </Box>
          )}
          <TournamentDetails
            tournament={selectedTournamentDetails}
            username={currentUsername ?? ''}
            seasons={seasons}
            isLoading={isTournamentDetailsLoading}
            onDeselectTournament={handleDeselectTournament}
            onLeaveTournamentResponse={handleLeaveTournamentResponse}
            onUpsertTournamentResponse={handleUpsertTournamentResponse}
          />
        </Grid>
      </Grid>

      {/* Modals */}
      <CreateTournamentModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateTournament={handleCreateTournament}
        seasons={seasons}
      />
      <SearchTournamentModal
        open={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        tournaments={searchModalTournaments}
        isLoading={searchLoading}
        onJoinTournament={handleJoinTournament}
        onRefresh={handleRefreshTournaments}
      />
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Tournaments Updated"
        message={successMessage}
        autoHideDuration={3000}
      />
      <ReusableAlert
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        severity="warning"
        text={snackbarText}
      />
    </Container>
  );
};

export default TournamentPage;
